import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, MapPin, GraduationCap, Loader2, Building2, MessageCircle, Link2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { Input } from "../ui/input";
import { Button } from "../ui/button";

export default function CourseFinder({ externalQuery } = {}) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("");
  const [level, setLevel] = useState("");
  const [university, setUniversity] = useState("");
  const [filters, setFilters] = useState({ countries: [], levels: [], universities: [] });
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSug, setShowSug] = useState(false);
  const sugRef = useRef(null);

  useEffect(() => { api.get("/public/course-filters").then((r) => setFilters(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    const onDoc = (e) => { if (sugRef.current && !sugRef.current.contains(e.target)) setShowSug(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const allUnis = filters.universities || [];
  const ql = q.trim().toLowerCase();

  // Universities providing the typed/selected course, in the chosen country — sorted by country then name.
  const uniOptions = useMemo(() => {
    return allUnis
      .filter((u) => !country || (u.countries || []).includes(country))
      .filter((u) => !level || (u.courses || []).some((c) => c.level === level))
      .filter((u) => !ql || u.name.toLowerCase().includes(ql) || (u.courses || []).some((c) => c.name.toLowerCase().includes(ql)))
      .sort((a, b) => ((a.countries || [])[0] || "zz").localeCompare((b.countries || [])[0] || "zz") || a.name.localeCompare(b.name));
  }, [allUnis, country, level, ql]);

  // Course suggestions limited to the chosen country/university — unfolds as you type.
  const courseSuggestions = useMemo(() => {
    const src = allUnis
      .filter((u) => !country || (u.countries || []).includes(country))
      .filter((u) => !university || u.id === university);
    const names = new Set();
    src.forEach((u) => (u.courses || []).forEach((c) => {
      if ((!level || c.level === level) && (!ql || c.name.toLowerCase().includes(ql))) names.add(c.name);
    }));
    return [...names].sort().slice(0, 8);
  }, [allUnis, country, level, university, ql]);

  // If the currently selected university drops out of the filtered list, clear it.
  useEffect(() => {
    if (university && !uniOptions.some((u) => u.id === university)) setUniversity("");
  }, [uniOptions, university]);

  // Group universities by country for the dropdown.
  const uniGroups = useMemo(() => {
    const g = {};
    uniOptions.forEach((u) => { const c = (u.countries || [])[0] || "Other"; (g[c] = g[c] || []).push(u); });
    return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
  }, [uniOptions]);

  const runSearch = async (p) => {
    const qq = (p.q || "").trim();
    if (qq.length < 2 && !p.country && !p.level && !p.university) return;
    setShowSug(false);
    setLoading(true);
    try {
      const params = new URLSearchParams({ q: qq, country: p.country || "", level: p.level || "", university: p.university || "" });
      const { data } = await api.get(`/public/course-search?${params.toString()}`);
      setRes(data);
    } catch (_) { setRes([]); }
    finally { setLoading(false); }
  };
  const search = () => runSearch({ q, country, level, university });

  // Pick up a search triggered from the hero search bar.
  useEffect(() => {
    if (!externalQuery) return;
    setQ(externalQuery.q || "");
    setCountry(externalQuery.country || "");
    setLevel(externalQuery.level || "");
    setUniversity(externalQuery.university || "");
    runSearch(externalQuery);
    // eslint-disable-next-line
  }, [externalQuery]);

  const pickCourse = (name) => { setQ(name); setShowSug(false); };

  const selCls = "h-12 rounded-full border border-input bg-white px-4 text-sm text-slate-700 min-w-[140px]";

  return (
    <section id="course-finder" className="py-20 md:py-28 bg-[#f4f8f6]" data-testid="course-finder">
      <div className="max-w-5xl mx-auto px-5">
        <p className="text-[#c9a227] font-semibold tracking-[0.2em] text-xs uppercase">Find your course</p>
        <h2 className="font-display text-3xl md:text-4xl font-bold text-[#012a1e] mt-2 max-w-2xl">Search by course, country or university</h2>
        <p className="text-slate-500 mt-3 max-w-xl">Type a course, specialisation or university name and filter by country, level and institution — we'll show you the partner universities <span className="font-medium">and their affiliated colleges</span> offering it.</p>

        <div className="flex flex-col gap-2 mt-7">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1" ref={sugRef}>
              <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="course-finder-input" value={q}
                onChange={(e) => { setQ(e.target.value); setShowSug(true); }}
                onFocus={() => setShowSug(true)}
                onKeyDown={(e) => e.key === "Enter" && search()}
                placeholder="e.g. Computer Science, UCSI, MBBS…" className="pl-10 h-12 rounded-full" autoComplete="off" />
              {showSug && courseSuggestions.length > 0 && (
                <div className="absolute z-20 left-0 right-0 mt-2 bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden" data-testid="course-finder-suggestions">
                  {courseSuggestions.map((name) => (
                    <button key={name} type="button" onMouseDown={() => pickCourse(name)}
                      className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-[#f0f6f3] flex items-center gap-2" data-testid={`course-suggestion-${name}`}>
                      <GraduationCap size={15} className="text-[#004030]" /> {name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button data-testid="course-finder-btn" onClick={search} disabled={loading} className="bg-[#004030] hover:bg-[#013d2b] text-white rounded-full px-7 h-12 font-semibold">
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Search"}
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            <select data-testid="course-finder-country" value={country} onChange={(e) => setCountry(e.target.value)} className={selCls}>
              <option value="">All countries</option>
              {filters.countries.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select data-testid="course-finder-level" value={level} onChange={(e) => setLevel(e.target.value)} className={selCls}>
              <option value="">All levels</option>
              {(filters.levels.length ? filters.levels : ["Foundation", "Diploma", "Undergraduate", "Postgraduate", "PhD"]).map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
            <select data-testid="course-finder-university" value={university} onChange={(e) => setUniversity(e.target.value)} className={`${selCls} max-w-[240px]`}>
              <option value="">{uniOptions.length ? "All universities" : "No universities match"}</option>
              {uniGroups.map(([cn, us]) => (
                <optgroup key={cn} label={cn}>
                  {us.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </optgroup>
              ))}
            </select>
          </div>
        </div>

        {res && (
          <div className="mt-8" data-testid="course-finder-results">
            {res.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center">
                <p className="text-slate-600">No matching courses found for this search.</p>
                <Button data-testid="course-finder-counsellor-empty" onClick={() => navigate("/contact")} className="mt-4 bg-[#c9a227] hover:bg-[#e0c05a] text-[#013d2b] rounded-full px-6 font-bold">
                  <MessageCircle size={16} className="mr-1.5" /> Talk to a counsellor
                </Button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                  <p className="text-sm text-slate-500">{res.length} result{res.length > 1 ? "s" : ""}</p>
                  <Button data-testid="course-finder-counsellor" onClick={() => navigate("/contact")} variant="outline" className="rounded-full border-[#004030] text-[#004030] hover:bg-[#004030] hover:text-white">
                    <MessageCircle size={15} className="mr-1.5" /> Talk to a counsellor
                  </Button>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {res.map((r, i) => {
                    const isInst = r.type === "institution";
                    const Wrap = r.university_slug ? "a" : "div";
                    return (
                      <Wrap key={i} {...(r.university_slug ? { href: `/universities/${r.university_slug}` } : {})} className="bg-white rounded-2xl border border-slate-100 p-5 hover:border-[#004030] hover:shadow-md transition-all block">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-[#004030]"><GraduationCap size={18} /><span className="font-semibold text-sm text-[#012a1e]">{r.course}</span></div>
                          <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${isInst ? "bg-amber-100 text-amber-700" : "bg-[#e6f0ec] text-[#004030]"}`}>{isInst ? "College" : "University"}</span>
                        </div>
                        {r.level && <span className="inline-block mt-1.5 text-[10px] bg-[#004030] text-white px-2 py-0.5 rounded-full">{r.level}{r.stream ? ` · ${r.stream}` : ""}</span>}
                        <p className="text-sm font-medium text-[#012a1e] mt-2 flex items-center gap-1.5"><Building2 size={14} className="text-slate-400" />{r.provider || r.university}</p>
                        {(r.campus || r.city || r.country) && <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><MapPin size={12} />{[r.campus, r.city, r.country].filter(Boolean).join(" · ")}</p>}
                        {isInst && (r.affiliated_to || []).length > 0 && <p className="text-[11px] text-slate-500 mt-1.5 flex items-center gap-1"><Link2 size={11} /> Affiliated: {r.affiliated_to.join(", ")}</p>}
                        {(r.fields || []).length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-3">{r.fields.slice(0, 4).map((f) => <span key={f} className="text-[10px] bg-[#f0f6f3] text-[#004030] px-2 py-0.5 rounded-full">{f}</span>)}</div>
                        )}
                      </Wrap>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
